import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import useVoyagerStore from '../../stores/useVoyagerStore';
import { agentErrorMessage, type AgentChatTurn, type ChatReply } from './api';
import ChatComposer from '../LandingAgent/ChatComposer';
import Promises from './Promises';
import Tooltip from '@mui/material/Tooltip';
import { useNumberPopover } from '../Ladder/NumberPopover';
import ClaimValue from '../Ladder/ClaimValue';
import { tileClaim, type LadderAnswer } from '../Ladder/claims';
import type { AgentClause, ComposedAgentReply } from '../../apis';

const OP_GLYPH: Record<string, string> = { '<=': '≤', '>=': '≥' };
const metricName = (qid: string) => qid.split('.').pop() || qid;
const num = (v: number | null | undefined) => (v == null || Number.isNaN(v) ? '—' : Number.isInteger(v) ? v.toLocaleString() : Number(v.toFixed(1)).toLocaleString());

/** The compact status strip under an agent turn: where it stands as of that reply. */
function StatusStrip({ status }: { status: NonNullable<ChatReply['status']> }) {
  return (
    <Box data-testid="chat-status" sx={{ display: 'grid', gap: 0.5 }}>
      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }} data-testid="chat-status-objective">{status.objective_label}</Typography>
        {status.pace && <Chip size="small" label={status.pace} data-testid="chat-status-pace" sx={{ height: 18, fontSize: '0.62rem', fontWeight: 600, bgcolor: 'var(--sunk)' }} />}
        {status.attainment != null && (
          <Chip size="small" label={`${Math.round(status.attainment * 100)}%`} data-testid="chat-status-attainment" sx={{ height: 18, fontSize: '0.62rem', fontWeight: 600, bgcolor: 'var(--sunk)' }} />
        )}
        {status.on_pace != null && (
          <Chip size="small" label={status.on_pace ? 'on pace ✓' : 'off pace ✗'} data-testid="chat-status-on-pace" data-on-pace={status.on_pace ? 'true' : 'false'}
            sx={{ height: 18, fontSize: '0.62rem', fontWeight: 600, bgcolor: status.on_pace ? 'var(--ok-tint)' : 'var(--bad-tint)', color: status.on_pace ? 'var(--ok)' : 'var(--bad)' }} />
        )}
        <Chip size="small" label={`${status.pending_proposals} pending`} data-testid="chat-status-pending" sx={{ height: 18, fontSize: '0.62rem', fontWeight: 600, bgcolor: 'var(--sunk)' }} />
        {status.top_lever && <Typography variant="caption" sx={{ color: 'text.secondary' }} data-testid="chat-status-lever">lever: {status.top_lever}</Typography>}
        {(status.guardrails || []).map((g, i) => (
          <Chip
            key={i}
            size="small"
            data-testid="chat-status-guardrail"
            data-breached={g.breached == null ? 'unknown' : g.breached ? 'true' : 'false'}
            label={`${metricName(g.metric)} ${num(g.value)} ${OP_GLYPH[g.op] || g.op} ${g.bound.toLocaleString()}`}
            title={g.note || undefined}
            sx={{ height: 18, fontSize: '0.62rem', fontWeight: 600,
                  bgcolor: g.breached ? 'var(--bad-tint)' : g.breached === false ? 'var(--ok-tint)' : 'var(--sunk)',
                  color: g.breached ? 'var(--bad)' : g.breached === false ? 'var(--ok)' : 'var(--ink-2)' }}
          />
        ))}
      </Box>
      {status.conflicts?.length > 0 && (
        <Typography variant="caption" sx={{ color: 'var(--warn)' }} data-testid="chat-status-conflicts">
          It has changed its mind on: {status.conflicts.join(' · ')}
        </Typography>
      )}
    </Box>
  );
}

export function QuoteClause({ clause }: { clause: Extract<AgentClause, { kind: 'quote' }> }) {
  const { open } = useNumberPopover();
  const claim = { id: clause.slot, label: clause.span.document_id, value: clause.span.quote,
    span: clause.span, confidence: 0, confidence_basis: clause.tier, verdict: 'derived' };
  return <Tooltip title={clause.span.document_id}>
    <Chip component="span" size="small" label={`“${clause.text}”${clause.check ? ` · ${clause.check.verdict}` : ''}`} data-testid="quote-clause"
      onClick={(e) => open(claim, e.currentTarget)}
      sx={{ height: 'auto', maxWidth: '100%', verticalAlign: 'middle', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 } }} />
  </Tooltip>;
}

function ComposedTurn({ turn }: { turn: ComposedAgentReply }) {
  if ((turn as any).redacted || (turn.kind === 'answer' && !turn.text && !turn.template)) {
    // a reopened conversation restores an answer by its references only (#296): the words are re-read through the
    // viewer's keys when asked again, never from the cache
    return <Typography variant="caption" color="text.secondary" data-testid="composed-redacted">
      This answer is not kept in the conversation cache — ask again to hear it.
    </Typography>;
  }
  const clauses = turn.clauses || [];
  const tiles = clauses.flatMap((c) => c.kind === 'number' ? [c.claim] : []);
  const ladder: LadderAnswer = { version: 1, question: '', perspective: '', status: 'completed',
    what: { tiles, tables: [], highlights: [] } };
  return <>
    <Typography component="div" sx={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>
      {turn.kind === 'refused' || turn.kind === 'handoff' ? (turn.reason || turn.text) : (turn.template || turn.text).split(/(\[\[c[1-9]\d*\]\])/).map((part, i) => {
        const clause = clauses.find((c) => c.slot === part);
        if (!clause) return <React.Fragment key={i}>{part}</React.Fragment>;
        if (clause.kind === 'quote') return <QuoteClause key={i} clause={clause} />;
        // how much of a cut was said — no claim of its own to open, and the one
        // part of the answer that must not read as another number (#368)
        if (clause.kind === 'coverage' || clause.kind === 'relationship') return <Box key={i} component="span" sx={{ color: 'text.secondary' }}>{clause.text}</Box>;
        if (clause.kind === 'number') return <ClaimValue key={i} claim={tileClaim(ladder, tiles.indexOf(clause.claim))}>{clause.text}</ClaimValue>;
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </Typography>
    <MadeLine made={turn.made} />
  </>;
}

export function MadeLine({ made }: { made: ComposedAgentReply['made'] }) {
  const clock = (at: string | null) => at ? at.slice(11, 16) : 'unavailable';
  return <Typography variant="caption" color="text.secondary" data-testid="how-it-was-made">
    {made.agent} · briefing {clock(made.briefing_at)} · warehouse {clock(made.warehouse_at)} · at your bar{made.gated ? ' (gated)' : ''}
  </Typography>;
}

const NO_TURNS: AgentChatTurn[] = [];

/**
 * A chat with an agent (#202): steering. Everything said here is signed
 * with the speaker's identity and read by the agent before its next run.
 * The transcript is the thread's own snapshot — client-owned, like a
 * conversation's — and the agent opens with where it stands.
 *
 * The store owns the turns and their delivery (`openChatTurn`,
 * `deliverChatTurn`): this screen only renders the snapshot and keeps the
 * in-flight indicators, so a reply that lands after a switch to another
 * tab is still recorded and is there on return.
 */
export default function AgentChat({ threadId }: { threadId: string }) {
  const thread = useVoyagerStore((s) => s.savedThreads.find((t) => t.id === threadId));
  const turns = useVoyagerStore((s) => (s.savedThreads.find((t) => t.id === threadId)?.landingAgentTurns as AgentChatTurn[] | undefined) || NO_TURNS);
  const agentName = useVoyagerStore((s) => {
    const t = s.savedThreads.find((x) => x.id === threadId);
    return (t?.sourceId && s.agents.find((a) => a.id === t.sourceId)?.name) || (t?.title || '').replace(/^Chat · /, '') || 'Agent';
  });
  const openChatTurn = useVoyagerStore((s) => s.openChatTurn);
  const deliverChatTurn = useVoyagerStore((s) => s.deliverChatTurn);
  const unsaved = useVoyagerStore((s) => !!s.agentChatUnsaved[threadId]);
  const navigateToAgentWall = useVoyagerStore((s) => s.navigateToAgentWall);
  const agentId = thread?.sourceId || null;
  // Until the agent has opened (or failed to), nothing can be sent: a line
  // sent meanwhile would race the opening for the first slot.
  const [opening, setOpening] = useState<'pending' | 'done'>(() => (agentId && turns.length === 0 ? 'pending' : 'done'));
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (opening !== 'pending' || !agentId) return;
    openChatTurn(threadId, agentId)
      .catch((e) => setError(agentErrorMessage(e, 'The agent did not answer')))
      .finally(() => setOpening('done'));
  }, [agentId, opening, threadId, openChatTurn]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns.length]);

  const ready = opening === 'done' && !!agentId;

  /**
   * Deliver one user turn through the store. Its `op_id` is minted once
   * and reused on every retry, so a line the network dropped is written by
   * the server once however many times it is resent; a failure keeps the
   * turn on screen, marked, with the retry beside it.
   */
  const deliver = async (turn: AgentChatTurn) => {
    setError(null); setSending(true);
    try {
      await deliverChatTurn(threadId, agentId as string, turn);
    } catch (e: any) {
      setError(agentErrorMessage(e, 'The agent did not answer'));
    } finally {
      setSending(false);
    }
  };

  const retry = (turn: AgentChatTurn) => {
    if (sending || !ready) return;
    deliver(turn);
  };

  if (!thread) return null;

  return (
    <Box data-testid="agent-chat" data-agent-id={agentId || ''} data-opening={opening} sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
      <Box data-testid="agent-chat-bar" sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0, flexWrap: 'wrap' }}>
        <Link component="button" type="button" underline="hover" onClick={() => { if (agentId) navigateToAgentWall(agentId); }} data-testid="agent-chat-name" sx={{ fontSize: 13.5, fontWeight: 700, color: 'text.primary', font: 'inherit' }}>
          {agentName}
        </Link>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Chat · steering — everything you say here is signed and read before its next run</Typography>
      </Box>

      <Box ref={listRef} data-testid="agent-chat-turns" sx={{ flex: 1, overflow: 'auto', p: 3, display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: '100%', maxWidth: 820, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {turns.map((t) => (
            <Box
              key={t.id}
              data-testid="agent-chat-turn"
              data-role={t.role}
              sx={{
                alignSelf: t.role === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '85%',
                px: 1.5, py: 1,
                borderRadius: 2.5,
                bgcolor: t.role === 'user' ? 'var(--accent-tint)' : 'background.paper',
                border: '1px solid', borderColor: t.role === 'user' ? 'var(--accent-tint)' : 'divider',
                display: 'grid', gap: 0.75,
              }}
            >
              {t.role === 'agent' && t.kind && t.made && t.session_id ? <ComposedTurn turn={t as ComposedAgentReply} /> : <>
                <Typography sx={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{t.text}</Typography>
                {t.role === 'agent' && t.status && <StatusStrip status={t.status} />}
                {t.role === 'agent' && t.made && <MadeLine made={t.made} />}
              </>}
              {t.role === 'user' && t.failed && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }} data-testid="agent-chat-undelivered">
                  <Typography variant="caption" sx={{ color: 'var(--bad)' }}>not delivered</Typography>
                  <Button size="small" variant="text" disabled={sending || !ready} onClick={() => retry(t)} data-testid="agent-chat-retry" sx={{ minWidth: 0, py: 0, fontSize: 12 }}>retry</Button>
                </Box>
              )}
            </Box>
          ))}
          {agentId && <Promises agentId={agentId} threadId={threadId} excludeIds={turns.map((t) => t.id)} />}
          {opening === 'pending' && turns.length === 0 && <Typography variant="caption" sx={{ color: 'text.disabled' }} data-testid="agent-chat-opening">{agentName} is opening…</Typography>}
          {sending && <Typography variant="caption" sx={{ color: 'text.disabled' }} data-testid="agent-chat-thinking">{agentName} is thinking…</Typography>}
          {error && <Typography variant="caption" color="error" data-testid="agent-chat-error">{error}</Typography>}
          {unsaved && <Typography variant="caption" sx={{ color: 'var(--warn)' }} data-testid="agent-chat-unsaved">not saved yet — retrying</Typography>}
        </Box>
      </Box>

      <Box data-testid="agent-chat-composer" sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
        <Box sx={{ width: '100%', maxWidth: 820 }}><ChatComposer variant="docked" draftKey={threadId} initialAddress={`@${agentName} `} attachments={false} disabledReason={!ready ? `Waiting for ${agentName}…` : undefined} /></Box>
      </Box>
    </Box>
  );
}
